import { apiFetch, buildApiUrl } from '../../utils/apiFetch';
import { getCookie } from '../../utils/cookies';

const normalizeOrder = (order = {}) => ({
  ...order,
  order_id:       order.order_id ?? order.id,
  order_number:   order.order_number ?? `#${order.order_id ?? order.id}`,
  status:         (order.order_status ?? order.status ?? 'PENDING').toUpperCase(),
  payment_status: (order.payment_status ?? 'UNPAID').toUpperCase(),
  payment_method: order.payment_method ?? 'COD',
  total:          parseFloat(order.total_amount ?? order.total ?? 0),
  created_at:     order.created_at ?? new Date().toISOString(),
  items:          order.items ?? [],
});

export const createOrdersSlice = (set, get) => ({
  orders:              [],
  orders_loading:      false,
  orders_error:        null,
  current_order:       null,
  place_order_loading: false,
  place_order_error:   null,

  // GET /api/v1/customer/orders
  fetchOrders: async ({ page = 1, limit = 20, status } = {}) => {
    set({ orders_loading: true, orders_error: null });
    try {
      const params = new URLSearchParams({ page, limit });
      if (status) params.set('status', status);
      const json   = await apiFetch(`api/v1/customer/orders?${params}`);
      const orders = (json.data?.orders ?? json.data ?? []).map(normalizeOrder);
      set({ orders, orders_loading: false });
      return orders;
    } catch (err) {
      set({ orders_error: err.message, orders_loading: false });
      return [];
    }
  },

  // POST /api/v1/customer/orders/place
  // Body: { address_id, payment_method, order_type, notes? }
  placeOrder: async ({ address_id, payment_method, notes = '', buyNowItem = null }) => {
    const token = getCookie('authToken');
    if (!token) throw new Error('Please login to place an order');

    set({ place_order_loading: true, place_order_error: null });

    const CART_BASE = buildApiUrl('api/v1/customer/cart');

    // ── Buy Now: swap server cart → single item → place → restore on fail ──
    let snapshotItems = null;
    if (buyNowItem) {
      try {
        const snap    = await apiFetch('api/v1/customer/cart');
        snapshotItems = snap.data?.items ?? snap.data?.cart_items ?? [];

        await apiFetch('api/v1/customer/cart/clear', { method: 'DELETE' });

        const addPayload = {
          product_id: Number(buyNowItem.product_id),
          quantity:   Number(buyNowItem.quantity),
        };
        if (buyNowItem.product_variant_id) addPayload.product_variant_id = Number(buyNowItem.product_variant_id);
        await apiFetch('api/v1/customer/cart/add', { method: 'POST', body: addPayload });
      } catch (err) {
        set({ place_order_error: err.message, place_order_loading: false });
        throw err;
      }
    }

    try {
      const json = await apiFetch('api/v1/customer/orders/place', {
        method: 'POST',
        body: {
          address_id:     Number(address_id),
          payment_method,
          order_type:     'DELIVERY',
          ...(notes?.trim() ? { notes: notes.trim() } : {}),
        },
      });

      const order = normalizeOrder(json.data.order);

      set((state) => ({
        orders:              [order, ...state.orders],
        current_order:       order,
        place_order_loading: false,
        place_order_error:   null,
      }));

      return {
        order,
        items:              json.data.items              ?? [],
        payment_session_id: json.data.payment_session_id ?? null,
        cashfree_order_id:  json.data.cashfree_order_id  ?? null,
        cf_order_status:    json.data.cf_order_status    ?? null,
      };

    } catch (err) {
      set({ place_order_error: err.message, place_order_loading: false });

      // Restore cart on failure (Buy Now only)
      if (buyNowItem && snapshotItems !== null) {
        try {
          await apiFetch('api/v1/customer/cart/clear', { method: 'DELETE' });
          for (const item of snapshotItems) {
            const p = { product_id: Number(item.product_id), quantity: Number(item.quantity ?? 1) };
            if (item.product_variant_id) p.product_variant_id = Number(item.product_variant_id);
            await apiFetch('api/v1/customer/cart/add', { method: 'POST', body: p });
          }
          get().fetchCart?.();
        } catch { /* best-effort */ }
      }

      throw err;
    }
  },

  // GET /api/v1/customer/orders/:id
  getOrderDetails: async (orderId) => {
    try {
      const json  = await apiFetch(`api/v1/customer/orders/${orderId}`);
      const order = normalizeOrder(json.data?.order ?? json.data ?? {});
      set({ current_order: order });
      return { ...order, items: json.data?.items ?? json.data?.order?.items ?? [] };
    } catch (err) {
      console.error('getOrderDetails:', err);
      return null;
    }
  },

  // POST /api/v1/customer/orders/:id/cancel
  cancelOrder: async (orderId, remarks = 'Cancelled by customer') => {
    set({ orders_loading: true });
    try {
      await apiFetch(`api/v1/customer/orders/${orderId}/cancel`, { method: 'POST', body: { remarks } });
      set((state) => ({
        orders: state.orders.map((o) =>
          String(o.order_id) === String(orderId) ? { ...o, status: 'CANCELLED' } : o
        ),
        orders_loading: false,
      }));
    } catch (err) {
      set({ orders_error: err.message, orders_loading: false });
      throw err;
    }
  },

  // POST /api/v1/customer/orders/:id/pay
  retryPayment: async (orderId) => {
    try {
      const json = await apiFetch(`api/v1/customer/orders/${orderId}/pay`, { method: 'POST' });
      return json.data;
    } catch (err) {
      console.error('retryPayment:', err);
      throw err;
    }
  },

  // GET /api/v1/customer/orders/:id/verify-payment
  verifyPayment: async (orderId) => {
    try {
      const json = await apiFetch(`api/v1/customer/orders/${orderId}/verify-payment`);
      const data = json.data ?? {};
      const orderData = data.order ?? data;
      const order = orderData?.order_id || orderData?.id ? normalizeOrder(orderData) : null;
      const payment_status = (
        data.payment_status ??
        data.paymentStatus ??
        order?.payment_status ??
        ''
      ).toUpperCase();

      if (order) {
        set((state) => ({
          current_order: order,
          orders: state.orders.map((item) =>
            String(item.order_id) === String(order.order_id) ? order : item
          ),
        }));
      } else if (payment_status) {
        set((state) => ({
          orders: state.orders.map((item) =>
            String(item.order_id) === String(orderId)
              ? { ...item, payment_status }
              : item
          ),
        }));
      }

      return { ...data, order, payment_status };
    } catch (err) {
      console.error('verifyPayment:', err);
      throw err;
    }
  },
});
